import { React, useState, useEffect } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTrash,
  faEye,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";
import { format } from "date-fns";
import { Toaster, toast } from "react-hot-toast";
import { PropagateLoader } from "react-spinners";
import { TablePagination } from "@mui/material";
import { updateInventoryRegistrationStatus } from "actions/action";
import { jwtDecode } from "jwt-decode";
import { Base_URL } from "../../actions/Base_URL"; // This line remains as it is the correct import

export default function AssignInventory() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("status");
  const [sortOrder, setSortOrder] = useState("asc");
  const [viewFlag, setViewFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(1);
  const [updatingStatus, setUpdatingStatus] = useState({});

  const userRole = (() => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.role?.toLowerCase();
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    getTableData();
  }, []);

  const filteredData = (Array.isArray(data) ? data : []).filter((item) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    
    const statusMatch = item.status && item.status.toLowerCase().includes(term);
    const invNameMatch = item.inventory_details?.name && item.inventory_details.name.toLowerCase().includes(term);
    const userNameMatch = item.user_details?.name && item.user_details.name.toLowerCase().includes(term);
    
    return statusMatch || invNameMatch || userNameMatch;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    let valA, valB;

    if (sortColumn === "user_name") {
      valA = a.user_details?.name || a.user_name || "";
      valB = b.user_details?.name || b.user_name || "";
    } else if (sortColumn === "inventory_name") {
      valA = a.inventory_details?.name || a.inventory_name || "";
      valB = b.inventory_details?.name || b.inventory_name || "";
    } else {
      valA = a[sortColumn];
      valB = b[sortColumn];
    }

    if (valA === undefined || valA === null) valA = "";
    if (valB === undefined || valB === null) valB = "";

    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else {
      return sortOrder === "asc"
        ? (valA > valB ? 1 : valA < valB ? -1 : 0)
        : (valB > valA ? 1 : valB < valA ? -1 : 0);
    }
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleSort = (column) => {
    setSortOrder((prevOrder) =>
      sortColumn === column && prevOrder === "asc" ? "desc" : "asc"
    );
    setSortColumn(column);
  };

  async function getTableData() {
    try {
      setLoading(true);
      const loginToken = localStorage.getItem("token");
      // const getApiResponse = await axios.get("https://api.nvcti.in/api/inventory/registration/getAllInventoryRegistrations", {
      const getApiResponse = await axios.get(
        `${Base_URL}/api/inventory/registration/getAllInventoryRegistrations`,
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (getApiResponse.status == 200) {
        const payload = Array.isArray(getApiResponse.data)
          ? getApiResponse.data
          : Array.isArray(getApiResponse?.data?.data)
            ? getApiResponse.data.data
            : Array.isArray(getApiResponse?.data?.registrations)
              ? getApiResponse.data.registrations
              : [];
        setData(payload);
        setTotalItems(payload.length);
      } else {
        setData([]);
        setTotalItems(0);
      }

    } catch (error) {
      toast.error("Error fetching inventory", error);
    } finally {
      setLoading(false);
    }
  }

  async function deleteInventory(id) {
    try {
      const loginToken = localStorage.getItem("token");
      // const getApiResponse = await axios.delete("https://api.nvcti.in/api/inventory/registration/deleteInventoryRegistration/" + id, {
      const getApiResponse = await axios.delete(
        `${Base_URL}/api/inventory/registration/deleteInventoryRegistration/${id}`,
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (getApiResponse.status == 200) {
        getTableData();
      }
    } catch (error) {
      toast.error("Error fetching inventory:", error);
    }
  }

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete these records?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: ReactDOMServer.renderToString(
        <>
          Yes, delete it!
          <FontAwesomeIcon icon={faTrash} style={{ marginLeft: "10px" }} />
        </>
      ),
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteInventory(id);
        Swal.fire({
          title: "Deleted!",
          text: "Inventory record deleted successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    });
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingStatus((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await updateInventoryRegistrationStatus(id, newStatus);
      if (res?.err) throw new Error();
      toast.success(`Status updated to ${newStatus}`);
      await getTableData();
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [id]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "issued":
        return "text-blue-500";
      case "received":
        return "text-green-500";
      case "pending":
        return "text-yellow-500";
      case "rejected":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "issued":
        return "fas fa-check-circle";
      case "received":
        return "fas fa-check-double";
      case "pending":
        return "fas fa-clock";
      case "rejected":
        return "fas fa-times-circle";
      default:
        return "fas fa-question-circle";
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "issued":
        return "bg-blue-500/20 text-blue-300 border border-blue-500/50 font-semibold";
      case "received":
        return "bg-green-500/20 text-green-300 border border-green-500/50 font-semibold";
      case "pending":
        return "bg-yellow-500/20 text-yellow-300 border border-yellow-500/50 font-semibold";
      case "rejected":
        return "bg-red-500/20 text-red-300 border border-red-500/50 font-semibold";
      default:
        return "bg-gray-500/20 text-gray-300 border border-gray-500/50 font-semibold";
    }
  };
  const userId = localStorage.getItem("user_id");
  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />
      <div className="relative">
        <div className="flex justify-between items-center px-4 mt-6">
          <div>
            <h2 className="font-bold text-2xl text-white mb-1">
              Inventory Booking Management
            </h2>
            <p className="text-blueGray-300 text-sm">
              Manage inventory registrations and track their status
            </p>
          </div>
          {userRole !== "student" && (
            <Tooltip title="Create New Inventory Assignment" arrow>
              <button
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm rounded-lg shadow-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  const basePath = window.location.pathname.startsWith("/labincharge") ? "/labincharge" : "/admin";
                  navigate(`${basePath}/assign-create-inventory`, {
                    state: { type: "create", id: userId },
                  });
                }}
              >
                <i className="fas fa-plus mr-2"></i>
                Assign Inventory
              </button>
            </Tooltip>
          )}
        </div>
        <div className="px-6 mt-6 mb-6">
          <div className="bg-blueGray-700 rounded-lg shadow-sm border border-blueGray-600 p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-white"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search by status, user name, or inventory name..."
                  className="block w-full pl-10 pr-10 py-3 border border-blueGray-600 rounded-lg leading-5 bg-blueGray-800 placeholder-blueGray-400 text-white focus:outline-none focus:placeholder-blueGray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-white hover:text-blueGray-300 focus:outline-none"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white">
                  {filteredData.length} of {data.length} registrations
                </span>
              </div>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <PropagateLoader color="#141c52" size={18} />
          </div>
        ) : (
          <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-xl rounded-xl bg-blueGray-700 border border-blueGray-600">
            <div className="block w-full overflow-x-auto">
              <table className="items-center w-full bg-transparent border-collapse">
                <thead className="bg-blueGray-700">
                  <tr>
                    <th className="px-6 min-w-[80px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600">
                      #
                    </th>
                    <th
                      className="px-6 min-w-[200px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600 cursor-pointer hover:bg-blueGray-600 transition-colors"
                      onClick={() => handleSort("inventory_name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Inventory Name</span>
                        {sortColumn === "inventory_name" && (
                          <i
                            className={`fas fa-chevron-${sortOrder === "asc" ? "up" : "down"
                              } text-xs`}
                          ></i>
                        )}
                      </div>
                    </th>
                    <th className="px-6 min-w-[120px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600">
                      Total Qty
                    </th>
                    <th className="px-6 min-w-[140px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600">
                      Available Qty
                    </th>
                    <th
                      className="px-6 min-w-[180px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600 cursor-pointer hover:bg-blueGray-600 transition-colors"
                      onClick={() => handleSort("user_name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>User Name</span>
                        {sortColumn === "user_name" && (
                          <i
                            className={`fas fa-chevron-${sortOrder === "asc" ? "up" : "down"
                              } text-xs`}
                          ></i>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 min-w-[140px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600 cursor-pointer hover:bg-blueGray-600 transition-colors"
                      onClick={() => handleSort("phone")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Phone</span>
                        {sortColumn === "phone" && (
                          <i
                            className={`fas fa-chevron-${sortOrder === "asc" ? "up" : "down"
                              } text-xs`}
                          ></i>
                        )}
                      </div>
                    </th>
                    <th className="px-6 min-w-[150px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600">
                      Requested Qty
                    </th>
                    <th
                      className="px-6 min-w-[140px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600 cursor-pointer hover:bg-blueGray-600 transition-colors"
                      onClick={() => handleSort("issued_date")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Issued Date</span>
                        {sortColumn === "issued_date" && (
                          <i
                            className={`fas fa-chevron-${sortOrder === "asc" ? "up" : "down"
                              } text-xs`}
                          ></i>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 min-w-[140px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600 cursor-pointer hover:bg-blueGray-600 transition-colors"
                      onClick={() => handleSort("receiving_date")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Return Date</span>
                        {sortColumn === "receiving_date" && (
                          <i
                            className={`fas fa-chevron-${sortOrder === "asc" ? "up" : "down"
                              } text-xs`}
                          ></i>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 min-w-[140px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600 cursor-pointer hover:bg-blueGray-600 transition-colors"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === "status" && (
                          <i
                            className={`fas fa-chevron-${sortOrder === "asc" ? "up" : "down"
                              } text-xs`}
                          ></i>
                        )}
                      </div>
                    </th>
                    <th className="px-6 min-w-[200px] border border-solid py-4 text-sm font-bold text-left text-white border-blueGray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length ? (
                    currentItems.map((item, index) => (
                      <tr key={item.registration_id}>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {item?.inventory_details?.name}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {item?.inventory_details?.total_quantity ??
                              item?.total_quantity ??
                              "-"}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {item?.inventory_details?.available_quantity ??
                              item?.available_quantity ??
                              "-"}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {item?.user_details?.name}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {item?.user_details?.phone}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {item.quantity || 1}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 border-blueGray-700">
                          <span className="ml-3 font-bold text-white">
                            {format(new Date(item.issued_date), "dd-MM-yyyy")}
                          </span>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 border-blueGray-700">
                          <div className="flex flex-col">
                            <span className={`ml-3 font-bold ${item.status === "issued" && dayjs(item.receiving_date).isBefore(dayjs(), 'day') ? "text-red-500" : "text-white"}`}>
                              {format(
                                new Date(item.receiving_date),
                                "dd-MM-yyyy"
                              )}
                            </span>
                            {item.status === "issued" && dayjs(item.receiving_date).isBefore(dayjs(), 'day') && (
                              <span className="ml-3 mt-1 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white border border-red-700 uppercase tracking-tighter w-fit shadow-md">
                                <i className="fas fa-exclamation-triangle mr-1"></i>
                                Overdue
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                          <div className="flex items-center">
                            <div
                              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${getStatusBadgeStyle(
                                item.status
                              )}`}
                            >
                              <i
                                className={`${getStatusIcon(
                                  item.status
                                )} mr-1.5 text-sm`}
                              ></i>
                              <span className="capitalize">{item.status}</span>
                            </div>
                          </div>
                        </td>
                        <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                          <div className="flex items-center justify-center gap-3">
                            {/* Status Update Dropdown - HIDDEN FOR STUDENTS */}
                            {userRole !== "student" && (
                              <div className="relative">
                                <select
                                  value={item.status}
                                  onChange={(e) =>
                                    handleStatusUpdate(
                                      item.registration_id,
                                      e.target.value
                                    )
                                  }
                                  disabled={updatingStatus[item.registration_id]}
                                  className="appearance-none bg-blueGray-800 border border-blueGray-600 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:border-blueGray-500 transition-colors duration-200"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="issued">Issued</option>
                                  <option value="received">Received</option>
                                  <option value="rejected">Rejected</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                  <i className="fas fa-chevron-down text-gray-400 text-xs"></i>
                                </div>
                                {updatingStatus[item.registration_id] && (
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-1 items-center">
                              {userRole !== "student" && (
                                <>
                                  <Tooltip title="Edit Registration" arrow>
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.95 }}
                                      type="button"
                                      onClick={() => {
                                        const basePath = window.location.pathname.startsWith("/labincharge") ? "/labincharge" : "/admin";
                                        navigate(`${basePath}/assign-create-inventory`, {
                                          state: {
                                            type: "edit",
                                            id: item.registration_id,
                                          },
                                        });
                                      }}
                                      className="inline-flex items-center justify-center w-8 h-8 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200"
                                    >
                                      <FontAwesomeIcon
                                        icon={faPenToSquare}
                                        className="text-sm"
                                      />
                                    </motion.button>
                                  </Tooltip>

                                  <Tooltip title="Delete Registration" arrow>
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.95 }}
                                      type="button"
                                      onClick={() =>
                                        handleDelete(item.registration_id)
                                      }
                                      className="inline-flex items-center justify-center w-8 h-8 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200"
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        className="text-sm"
                                      />
                                    </motion.button>
                                  </Tooltip>
                                </>
                              )}

                              <Tooltip title="View Details" arrow>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => {
                                    if (userRole === "student") {
                                      navigate(`/student/viewInvRegistration`, {
                                        state: { invId: item.inventory_id },
                                      });
                                    } else {
                                      const basePath = window.location.pathname.startsWith("/labincharge") ? "/labincharge" : "/admin";
                                      navigate(`${basePath}/assign-create-inventory`, {
                                        state: {
                                          type: "view",
                                          id: item.registration_id,
                                        },
                                      });
                                    }
                                  }}
                                  className="inline-flex items-center justify-center w-8 h-8 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 hover:text-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1 transition-all duration-200"
                                >
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className="text-sm"
                                  />
                                </motion.button>
                              </Tooltip>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-12 border-blueGray-700">
                        <div className="flex flex-col items-center justify-center">
                          <i className="fas fa-inbox text-white text-4xl mb-4"></i>
                          <h3 className="text-lg font-medium text-white mb-2">
                            No registrations found
                          </h3>
                          <p className="text-blueGray-300 text-sm">
                            Try adjusting your search criteria or create a new
                            inventory assignment.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {currentItems.length > 0 && (
                <div className="flex justify-center mt-6 bg-blueGray-700 p-4 rounded-b-xl border-t border-blueGray-600">
                  <TablePagination
                    component="p"
                    count={totalItems} // total number of items, not pages
                    page={currentPage - 1} // MUI pages are 0-indexed
                    onPageChange={(event, newPage) =>
                      setCurrentPage(newPage + 1)
                    }
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={(event) => {
                      setItemsPerPage(parseInt(event.target.value, 10));
                      setCurrentPage(1);
                    }}
                    rowsPerPageOptions={[2, 5, 10, 25, 50]}
                    labelRowsPerPage="Items per page"
                    sx={{
                      "& .MuiTablePagination-toolbar": {
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        color: "white",
                      },
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      {
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        color: "white",
                      },
                      "& .MuiTablePagination-select": {
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        color: "white",
                      },
                      "& .MuiTablePagination-selectIcon": {
                        color: "white",
                      },
                      "& .MuiIconButton-root": {
                        color: "white",
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div >
    </>
  );
}
